/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import axios, { AxiosRequestConfig, AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { createMockAdapter } from 'data/api/mockAdapter';
import { readFromLocalStorageUserInfo } from '../reducers/userReducer';
import qs from 'query-string';
import { LocaleId } from '../translation/i18n';
import { AUTH_API_URLS_PREFIX } from './auth/authClient.typings';
import { getCsrfToken } from '../../utils/utils';

export const API_ENDPOINT = import.meta.env.VITE_API_PREFIX ? import.meta.env.VITE_API_PREFIX : 'pks-inspector';

class ApiClient {
    private axiosInstance: AxiosInstance;
    private localeId: LocaleId = readFromLocalStorageUserInfo() ? readFromLocalStorageUserInfo().localeId : LocaleId.En;

    private isRefreshingToken = false;
    private refreshQueue: ((token: string) => void)[] = [];

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: `/${API_ENDPOINT}`,
            paramsSerializer: (params) => qs.stringify(params),
            headers: {
                'User-Lang': this.localeId,
                'User-Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
                'X-CSRF-Token': getCsrfToken() ? getCsrfToken() : '',
            },
            withCredentials: true,
        });

        if (import.meta.env.VITE_MOCK_API) {
            // tslint:disable-next-line:no-console
            console.log('%c 🙊 mock API enabled. Use it only for development purpose', 'font-size: 16px');
            createMockAdapter(this.axiosInstance);
        }
        // When some code retry axios request baseUrl added twice
        // so we add this workaround to strip it
        // Currently we retry request in two cases:
        // - mock adapter when onAny().passThrough() used
        // - auth interceptor to retry load data after get new refresh token
        // See details here
        // https://github.com/ctimmerm/axios-mock-adapter/issues/185
        this.axiosInstance.interceptors.request.use((config) => {
            if (config.baseURL && config.url.startsWith(config.baseURL)) {
                config.url = config.url.slice(config.baseURL.length);
            }
            return config;
        });

        this.axiosInstance.interceptors.response.use((config) => {
            if (import.meta.env.VITE_API_PREFIX === 'pks-inspector' && !import.meta.env.VITE_MOCK_API) {
                if (
                    (config.config.url === '1.0.0/authentication/inspector' ||
                        config.config.url === '1.0.0/authentication/operator' ||
                        config.config.url === '1.0.0/authentication/taxpayer') &&
                    config.status === 200
                ) {
                    this.fetchCsrfToken().then((r) => {
                        this.axiosInstance.defaults.headers['X-CSRF-Token'] = r;
                    });
                }
            }

            return config;
        });

        this.axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const isCsrfError =
                    import.meta.env.VITE_API_PREFIX === 'pks-inspector' &&
                    !import.meta.env.VITE_MOCK_API &&
                    error.response?.status === 403 &&
                    error.config?.url !== '/csrf-token' &&
                    error.response?.data?.message === 'invalid csrf token';

                if (isCsrfError) {
                    const maxRetries = 3;
                    const retryCount = error.config.__retryCount || 0;

                    if (retryCount >= maxRetries) {
                        return Promise.reject(error);
                    }

                    if (!this.isRefreshingToken) {
                        this.isRefreshingToken = true;
                        error.config.__retryCount = retryCount + 1;

                        try {
                            const newToken = await this.fetchCsrfToken();
                            this.isRefreshingToken = false;
                            this.processRefreshQueue(newToken);

                            error.config.headers['X-CSRF-Token'] = newToken;

                            return this.axiosInstance.request(error.config);
                        } catch (refreshError) {
                            this.isRefreshingToken = false;
                            this.processRefreshQueue(null);

                            return Promise.reject(refreshError);
                        }
                    } else {
                        return new Promise((resolve) => {
                            this.refreshQueue.push((token) => {
                                error.config.headers['X-CSRF-Token'] = token;
                                resolve(this.axiosInstance.request(error.config));
                            });
                        });
                    }
                }

                return Promise.reject(error);
            },
        );

        this.initErrorsInterceptor();
    }

    private processRefreshQueue(token: string | null) {
        this.refreshQueue.forEach((promise) => (token ? promise(token) : promise('')));
        this.refreshQueue = [];
    }

    /** Test-only accessor for attaching axios-mock-adapter. Not used by app code. */
    public get axios(): AxiosInstance {
        return this.axiosInstance;
    }

    public setLocaleHeader(locale: LocaleId) {
        this.axiosInstance.defaults.headers['User-Lang'] = locale;
    }

    public async get<T>(url: string, params?: unknown): Promise<T> {
        const resp = await this.axiosInstance.get<T>(url, { params });
        return resp.data;
    }

    public async getWithConfig<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const resp = await this.axiosInstance.get<T>(url, config);
        return resp.data;
    }

    public async getWithFullResponse<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        if (config.headers) {
            config.headers = { ...this.axiosInstance.defaults.headers, ...config.headers };
        }

        return await this.axiosInstance.get<T>(url, config);
    }

    public async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        if (config && config.headers) {
            config.headers = { ...this.axiosInstance.defaults.headers, ...config.headers };
        }
        const resp = await this.axiosInstance.post<T>(url, data, config);
        return resp.data;
    }

    public async put<T>(url: string, data?: unknown, params?: unknown): Promise<T> {
        const resp = await this.axiosInstance.put<T>(url, data, { params });
        return resp.data;
    }

    public async patch<T>(url: string, data?: unknown, params?: unknown): Promise<T> {
        const resp = await this.axiosInstance.patch<T>(url, data, { params });
        return resp.data;
    }

    public async delete<T>(url: string, params?: unknown, data?: unknown): Promise<T> {
        const resp = await this.axiosInstance.delete<T>(url, { params, data });
        return resp.data;
    }

    public async downloadFile(url: string, params?: unknown): Promise<ArrayBuffer> {
        const resp = await this.axiosInstance.get<ArrayBuffer>(url, {
            params,
            responseType: 'arraybuffer',
        });
        return resp.data;
    }

    public async downloadFilePost(url: string, data?: unknown): Promise<ArrayBuffer> {
        const resp = await this.axiosInstance.post<ArrayBuffer>(url, data, { responseType: 'arraybuffer' });
        return resp.data;
    }

    public on401(fn: ApiClient['on401Callback']): void {
        this.on401Callback = fn;
    }

    private on401Callback: (err: AxiosError) => void = () => {
        /* nothing todo */
    };

    private initErrorsInterceptor(): void {
        this.axiosInstance.interceptors.response.use(null, (err: AxiosError) => {
            const resp = err.response;
            if (!resp) {
                return Promise.reject(err);
            }

            if (resp.status !== 401) {
                return Promise.reject(err);
            }

            if (this.isLoginRequest(resp.config)) {
                // We bypass errors on login page
                return Promise.reject(err);
            }

            // Here we handle all 401
            this.on401Callback(err);
            return Promise.reject(err);
        });
    }

    private isLoginRequest(req: AxiosRequestConfig): boolean {
        const excludeFrom401Handling = [
            '/authentication/inspector',
            '/authentication/operator',
            '/authentication/taxpayer',
            '/authentication/logout',
        ];

        return excludeFrom401Handling.some((url) => req.url?.startsWith(url) && req.method?.toUpperCase() === 'POST');
    }

    private async fetchCsrfToken(): Promise<string> {
        const resp = await axios.get<{ csrfToken: string }>(
            API_ENDPOINT + '/1.0.0' + AUTH_API_URLS_PREFIX + '/csrf-token',
        );
        const { csrfToken } = resp.data;

        return csrfToken;
    }
}

export default new ApiClient();
